import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { FaPrint, FaArrowLeft, FaSpinner } from 'react-icons/fa'
import InvoiceDocument, { printWhenReady } from '../components/InvoiceDocument'
import { issuerFromSettings, settingsMap } from '../lib/invoice'
import { INVOICING_IS_PREVIEW } from '../lib/features'
import * as api from '../lib/api'

/**
 * One document, alone on the page, ready to print.
 *
 * A route of its own rather than a print stylesheet over the editor. Hiding the
 * panel chrome with `display: none` works until it does not — a stray sticky
 * sidebar reappears on page two, a modal backdrop prints as a grey wash — and
 * the failure is invisible until a customer receives it. Here there is simply
 * nothing else on the page to go wrong.
 *
 * It also opens in a new tab, so printing never costs the operator the editor
 * state they were part way through.
 */
const InvoicePrintPage = () => {
  const { id } = useParams()
  const [invoice, setInvoice] = useState(null)
  const [issuer, setIssuer] = useState(null)
  const [error, setError] = useState(null)
  const printed = useRef(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const [document_, settingRows] = await Promise.all([
          api.getInvoice(id),
          api.getSettings().catch(() => []),
        ])
        if (cancelled) return
        setInvoice(document_)
        setIssuer(issuerFromSettings(settingsMap(settingRows)))
      } catch (err) {
        if (!cancelled) setError(err)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [id])

  // Open the print dialogue once the document is on screen and its images have
  // arrived — never before, or the PDF has a hole where the logo should be.
  useEffect(() => {
    if (!invoice || !issuer || printed.current) return
    printed.current = true
    printWhenReady()
  }, [invoice, issuer])

  if (error) {
    return (
      <div className="mx-auto max-w-lg p-8 text-center">
        <p className="text-[13px] text-ink">Could not load that document.</p>
        <p className="mt-1 text-2xs text-ink-muted">{error.message}</p>
      </div>
    )
  }

  if (!invoice || !issuer) {
    return (
      <div className="flex items-center justify-center gap-2.5 py-16 text-sm text-ink-muted">
        <FaSpinner className="animate-spin" aria-hidden="true" />
        Preparing the document…
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-plane py-6">
      {/* print-hide, not a media query on each element: one class, one rule. */}
      <div className="print-hide mx-auto mb-4 flex max-w-[210mm] flex-wrap items-center justify-between gap-2 px-4">
        <button onClick={() => window.close()} className="btn-ghost btn-sm">
          <FaArrowLeft className="text-[10px]" />
          Close this tab
        </button>
        <div className="flex items-center gap-2">
          <span className="text-2xs text-ink-muted">
            Choose “Save as PDF” in the print dialogue to e-mail it.
          </span>
          <button onClick={() => printWhenReady()} className="btn-primary btn-sm">
            <FaPrint className="text-[10px]" />
            Print
          </button>
        </div>
      </div>

      {/* print-hide, so the reminder is on the screen of the person about to
          send the document and never on the document itself. */}
      {INVOICING_IS_PREVIEW && (
        <p className="print-hide mx-auto mb-4 max-w-[210mm] px-4 text-2xs text-ink-muted">
          The invoice generator is still being built. Check the figures on this page before you send
          it to a customer.
        </p>
      )}

      <div className="mx-auto w-fit">
        <InvoiceDocument invoice={invoice} items={invoice.items ?? []} issuer={issuer} />
      </div>
    </div>
  )
}

export default InvoicePrintPage
